/**
 * XMS block schema convention (Phase 2).
 *
 * Every block in the library declares an editable-props *schema* — an ordered
 * list of typed fields — alongside a default props payload and presentation
 * metadata. The schema is the contract the later Phase-3 page builder consumes
 * to auto-generate authoring forms: it never needs to know a block's internals,
 * only its field list. The render component, by contrast, reads the open
 * `props` payload defensively (every value is treated as `unknown`) so an
 * out-of-date or hand-authored payload can never crash the page.
 *
 * This file defines TYPES only — no React, no registry imports beyond the
 * component prop type — so it stays a cheap, dependency-light contract.
 */

/** The entity kinds a `entity` field can point at (live-data spotlights). */
export type CmsEntityKind = "fbo" | "airport" | "flight_school" | "event";

/**
 * The editable field types an authoring UI must know how to render. `list` is a
 * repeatable group of scalar sub-fields (e.g. the cards in a Feature Cards
 * block); everything else is scalar.
 */
export type CmsFieldType =
  | "text"
  | "textarea"
  | "richtext"
  | "image"
  | "link"
  | "select"
  | "boolean"
  | "number"
  | "entity"
  | "global"
  | "list"
  | "tabs"
  | "blocks";

interface CmsFieldBase {
  /** Key into the block's `props` payload. */
  key: string;
  label: string;
  /** Optional author-facing helper text. */
  help?: string;
  required?: boolean;
}

export interface CmsTextField extends CmsFieldBase {
  type: "text" | "textarea";
  placeholder?: string;
}
export interface CmsRichTextField extends CmsFieldBase {
  /** Stored as `string[]` (one paragraph per entry). */
  type: "richtext";
}
export interface CmsImageField extends CmsFieldBase {
  /** Stored as a path under the public root (e.g. `images/x.jpg`) or a URL. */
  type: "image";
}
export interface CmsLinkField extends CmsFieldBase {
  /** Stored as a string href (internal `/path` or external `https://`). */
  type: "link";
}
export interface CmsBooleanField extends CmsFieldBase {
  type: "boolean";
}
export interface CmsNumberField extends CmsFieldBase {
  type: "number";
  min?: number;
  max?: number;
  step?: number;
}
export interface CmsSelectField extends CmsFieldBase {
  type: "select";
  options: { value: string; label: string }[];
}
export interface CmsEntityField extends CmsFieldBase {
  type: "entity";
  entity: CmsEntityKind;
}
export interface CmsGlobalField extends CmsFieldBase {
  /**
   * A reference to a global content item, stored as the global's normalized
   * key. The authoring UI renders a picker of published globals.
   */
  type: "global";
}

/** A field that can appear inside a `list` item (no nested lists). */
export type CmsScalarField =
  | CmsTextField
  | CmsRichTextField
  | CmsImageField
  | CmsLinkField
  | CmsSelectField
  | CmsBooleanField
  | CmsNumberField
  | CmsEntityField
  | CmsGlobalField;

export interface CmsListField extends CmsFieldBase {
  type: "list";
  /** Singular label for one item, e.g. "Card". */
  itemLabel: string;
  /** The scalar fields each item in the list exposes. */
  fields: CmsScalarField[];
  /** Optional max number of items the author may add. */
  max?: number;
}

/**
 * A `tabs` field: a repeatable list of tabs, each carrying a label plus its own
 * nested block list. The authoring UI renders a nested block composer per tab;
 * the value is stored as `{ label: string; blocks: CmsBlock[] }[]`.
 */
export interface CmsTabsField extends CmsFieldBase {
  type: "tabs";
  /** Singular label for one tab, e.g. "Tab". */
  itemLabel: string;
  /** Optional max number of tabs the author may add. */
  max?: number;
}

export interface CmsBlocksField extends CmsFieldBase {
  type: "blocks";
  /** Restrict collection templates to display-only registered blocks. */
  allowedTypes?: readonly string[];
}
export type CmsField =
  | CmsScalarField
  | CmsListField
  | CmsTabsField
  | CmsBlocksField;

/**
 * Coarse grouping used to organize the block palette in the page builder.
 */
export type BlockCategory =
  | "layout"
  | "media"
  | "marketing"
  | "entity"
  | "engagement";

export interface CmsBlockMetadata {
  type: string;
  label: string;
  description: string;
  category: BlockCategory;
  fields: CmsField[];
  defaults: Record<string, unknown>;
}

/** Display-only blocks supported inside bounded live directory collections. */
export const CMS_COLLECTION_TEMPLATE_TYPES = [
  "rich_text",
  "image",
  "cta",
] as const;
